import { Fragment } from 'react';
import { Helmet } from 'react-helmet';
import Image from 'components/Image';
import Footer from 'components/Footer';
import {
  ProjectBackground,
  ProjectContainer,
  ProjectHeader,
  ProjectImage,
  ProjectSection,
  ProjectSectionColumns,
  ProjectSectionContent,
  ProjectSectionHeading,
  ProjectSectionText,
  ProjectTextRow,
} from 'components/ProjectLayout';
import { useScrollRestore } from 'hooks';
import { media } from 'utils/style';
import prerender from 'utils/prerender';
import dttBackground from 'assets/images/pcb.jpg';
import dttBackgroundLarge from 'assets/images/pcb.jpg';
import dttBackgroundPlaceholder from 'assets/images/pcb-Placeholder.jpg';
import dtt from 'assets/images/techStack.jpg';
import dttLarge from 'assets/images/techStack.jpg';
import dttPlaceholder from 'assets/images/techStack-Placeholder.jpg';
import dttBranding from 'assets/images/techStackSecondary.jpg';
import dttBrandingLarge from 'assets/images/techStackSecondary.jpg';
import dttBrandingPlaceholder from 'assets/images/techStackSecondary-Placeholder.jpg';
import dttTool from 'assets/images/iot.jpg';
import dttToolLarge from 'assets/images/iot.jpg';
import dttToolPlaceholder from 'assets/images/iot-Placeholder.jpg';
import mobileDev from 'assets/images/mobile-dev.png';
import mobileDevLarge from 'assets/images/mobile-dev.png';
import mobileDevPlaceholder from 'assets/images/mobile-dev-placeholder.png';
import testing from 'assets/images/testing.png';
import testingLarge from 'assets/images/testing.png';
import testingPlaceholder from 'assets/images/testing-placeholder.png';
import {
  frontEndIcons,
  backEndIcons,
  mobileIcons,
  embeddedIcons,
  testingIcons,
} from '../../components/IconGrid/TechStackData';
import './index.css';
import IconGrid from '../../components/IconGrid/IconGrid';

const title = 'Technology stack';
const description =
  'Continuously learning new technologies and building upon my knowledge and experience.';
const roles = ['Front-end', 'Back-end', 'Mobile', 'Embedded', 'Testing'];

function DevTechTools() {
  useScrollRestore();

  return (
    <Fragment>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
      </Helmet>
      <ProjectContainer style={{ overflow: 'hidden' }}>
        <ProjectBackground
          srcSet={`${dttBackground} 1080w, ${dttBackgroundLarge} 2160w`}
          placeholder={dttBackgroundPlaceholder}
          entered={!prerender}
        />
        <ProjectHeader title={title} description={description} url="" roles={roles} />
        <ProjectSection first>
          <ProjectSectionContent>
            <ProjectImage
              raised
              srcSet={`${dtt} 1280w, ${dttLarge} 2560w`}
              placeholder={dttPlaceholder}
              sizes={`(max-width: ${media.mobile}px) 100vw, (max-width: ${media.tablet}px) 800px, 1000px`}
              alt="Landing screen of the DevTech Tools website."
            />
          </ProjectSectionContent>
        </ProjectSection>
        <ProjectSection>
          <ProjectSectionColumns>
            <ProjectTextRow>
              <ProjectSectionHeading>Front-end</ProjectSectionHeading>
              <ProjectSectionText>
                An overview of some of the tools that I have learned so far on the
                front-end side of development. Some of these frameworks and libraries are
                not completely mastered.
              </ProjectSectionText>
            </ProjectTextRow>
            <IconGrid icons={frontEndIcons} />
          </ProjectSectionColumns>
        </ProjectSection>

        <ProjectSection light>
          <ProjectSectionColumns>
            <ProjectTextRow noMargin>
              <ProjectSectionHeading>Back-end</ProjectSectionHeading>
              <ProjectSectionText>
                So far I have learned how to implement client-server systems that can work
                with relational databases. Implementing REST APIs and providing endpoints
                for other applications. Last but not least I have studied about several
                design patterns and I always follow the SOLID design principles.
              </ProjectSectionText>
            </ProjectTextRow>
            <Image
              srcSet={`${dttBranding} 400w, ${dttBrandingLarge} 898w`}
              placeholder={dttBrandingPlaceholder}
              sizes={`(max-width: ${media.mobile}px) 100vw, (max-width: ${media.tablet}px) 100vw, 50vw`}
              alt="The DevTech Tools color palette and logo, featuring pipelines as electronic traces."
            />
          </ProjectSectionColumns>

          <ProjectSectionColumns>
            <ProjectSectionContent>
              <ProjectSectionText />
              <IconGrid icons={backEndIcons} />
            </ProjectSectionContent>
          </ProjectSectionColumns>
        </ProjectSection>

        <ProjectSection>
          <ProjectSectionContent>
            <Image
              raised
              srcSet={`${mobileDev} 400w, ${mobileDevLarge} 898w`}
              placeholder={mobileDevPlaceholder}
              sizes={`(max-width: ${media.mobile}px) 100vw, (max-width: ${media.tablet}px) 100vw, 50vw`}
              alt="The tool wrapper of the JSON to CSV data converter."
            />
            <ProjectSectionText />
          </ProjectSectionContent>
          <ProjectSectionColumns alternate>
            <IconGrid icons={mobileIcons} />
            <ProjectTextRow noMargin>
              <ProjectSectionHeading>Mobile</ProjectSectionHeading>
              <ProjectSectionText>
                The technologies for developing mobile applications I have learned so far.
              </ProjectSectionText>
            </ProjectTextRow>
          </ProjectSectionColumns>
        </ProjectSection>

        <ProjectSection light>
          <ProjectSectionColumns>
            <ProjectTextRow noMargin>
              <ProjectSectionHeading>Embedded</ProjectSectionHeading>
              <ProjectSectionText>
                In the embedded world I have programmed mostly on Arduino. I have an
                ATMega2560 and I program only in C. I have gained knowledge on how to
                write my own drivers as well as concepts such as PWM, ADC and more.
                Furthermore I have worked on a project that utilizes a LoRaWaN transceiver
                and several sensors together with FreeRTOS for task synchronization.
              </ProjectSectionText>
            </ProjectTextRow>
            <Image
              srcSet={`${dttTool} 400w, ${dttToolLarge} 898w`}
              placeholder={dttToolPlaceholder}
              sizes={`(max-width: ${media.mobile}px) 100vw, (max-width: ${media.tablet}px) 100vw, 50vw`}
              alt="The DevTech Tools color palette and logo, featuring pipelines as electronic traces."
            />
          </ProjectSectionColumns>

          <ProjectSectionContent>
            <ProjectSectionText />
            <IconGrid icons={embeddedIcons} />
          </ProjectSectionContent>
        </ProjectSection>

        <ProjectSection>
          <ProjectSectionContent>
            <Image
              raised
              srcSet={`${testing} 400w, ${testingLarge} 898w`}
              placeholder={testingPlaceholder}
              sizes={`(max-width: ${media.mobile}px) 100vw, (max-width: ${media.tablet}px) 100vw, 50vw`}
              alt="The tool wrapper of the JSON to CSV data converter."
            />
            <ProjectSectionText />
          </ProjectSectionContent>
          <ProjectSectionColumns alternate>
            <IconGrid icons={testingIcons} />
            <ProjectTextRow noMargin>
              <ProjectSectionHeading>Testing</ProjectSectionHeading>
              <ProjectSectionText>
                An overview of the testing libraries and frameworks I have used so far.
              </ProjectSectionText>
            </ProjectTextRow>
          </ProjectSectionColumns>
        </ProjectSection>

      </ProjectContainer>
      <Footer />
    </Fragment>
  );
}

export default DevTechTools;
